import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import CpfInput from './CpfInput';
import AddressForm from './AddressForm';
import Select from 'react-select';
import  './ClinicPatientForm.module.scss';
import FormLabel from './FormLabel';
import PhoneNumberInput from './PhoneNumberInput';
import { alertModal } from '../utils/modalController';
import styles from './ClinicPatientForm.module.scss';
import { fetchAuth } from '../utils/fetchAuth';

export type ClinicPatientFormProps = {
  patient?: Patient;
};

const apiUrl = process.env.REACT_APP_API_URL || '';

const ClinicPatientForm: React.FC<ClinicPatientFormProps> = ({ patient }) => {
  const [sexos, setSexos] = useState<Sexo[]>([]);
  const [civilStatus, setCivilStatus] = useState<CivilStatus[]>([]);
  const [indication, setIndication] = useState<User[]>([]);

  const [users, setUsers] = useState<User[]>([]);
  const [selectedSexo, setSelectedSexo] = useState<number | null>(null);
  const [selectedIndicacao, setSelectedIndicacao] = useState<number | null>(null);
  const [selectedCivilStatus, setSelectedCivilStatus] = useState<number | null>(null);
  const [selectedResponsavel, setSelectedResponsavel] = useState<number | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [userAge, setUserAge] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true); // <-- Começa carregamento

      try {
        const [resSexos, resUsers, resPatients, resCivilStatus] = await Promise.all([
          fetch(apiUrl + '/contas/sexos'),
          fetchAuth(apiUrl + '/consultorio/persons'),
          fetchAuth(apiUrl + '/consultorio/patients'),
          fetch(apiUrl + '/contas/estados-civis')
        ]);

        if(!resSexos.ok) {
          const data = await resSexos.json();
          throw new Error(data.message || 'Erro ao carregar sexos');
        }

        if(!resCivilStatus.ok) {
          const data = await resCivilStatus.json();
          throw new Error(data.message || 'Erro ao carregar estados civis');
        }

        if(!resPatients.ok) {
          const data = await resPatients.json();
          throw new Error(data.message || 'Erro ao carregar pacientes');
        }

        if(!resUsers.ok) {
          const data = await resUsers.json();
          throw new Error(data.message || 'Erro ao carregar usuários');
        }

        const [dataSexos, dataUsers, dataPatients, dataCivilStatus] = await Promise.all([
          resSexos.json(), resUsers.json(), resPatients.json(), resCivilStatus.json()
        ]);

        setSexos(dataSexos);
        setUsers(dataUsers.data);
        setIndication(dataPatients.data);
        setCivilStatus(dataCivilStatus);

        if (patient) {
          const resPatient = await fetchAuth(`${apiUrl}/consultorio/patients/${patient.id}`);
          if (!resPatient.ok) {
            const data = await resPatient.json();
            throw new Error(data.message || 'Erro ao carregar dados do paciente');
          }
          const dataPatient = await resPatient.json();
          
          if(!dataPatient.user){
            throw new Error('Erro ao carregar dados do paciente');
          }
          const resUser = await fetchAuth(`${apiUrl}/consultorio/users/${dataPatient.user.id}`);
          if (!resUser.ok) {
            const data = await resUser.json();
            throw new Error(data.message || 'Erro ao carregar dados do usuário');
          }
          const dataUser = await resUser.json();

          setUserData({
            ...dataUser,
            id: dataUser.id ?? -1,
            name: dataUser.name || '',
            patient: {
              ...dataPatient,
              id: dataPatient.id ?? -1
            },
            modified: dataUser?.modified ?? ''
          });
          const matchedSexo = (dataSexos as Sexo[]).find(s => s.code === dataUser?.sexo);
          setSelectedSexo(matchedSexo?.code ?? null);
          setUserAge(dataUser?.birthday ? Math.floor((new Date().getTime() - new Date(dataUser.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365)) : null);
          setSelectedResponsavel(patient?.legal_responsible ?? null);
          setSelectedIndicacao(patient?.indicacao ?? null);
          setSelectedCivilStatus(dataUser?.civil_status ?? null);
        } else {
          setUserData(null);
          setSelectedSexo(null);
          setUserAge(null);
          setSelectedResponsavel(null);
          setSelectedIndicacao(null);
          setSelectedCivilStatus(null);
        }
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        alertModal('Erro ao carregar dados: ' + err);
      } finally {
        setIsLoading(false); // <-- Termina carregamento
      }
    };

    fetchAll();
  }, [patient]);

  return (
    <>
      <Form.Control type="hidden" name="id" id="id" defaultValue={userData?.id} />
      <Form.Group className="mb-3">
        <Row>
          <Col md={6}>
            <Form.Group>
              <FormLabel required={true} htmlFor='nome'>Nome</FormLabel>
              <Form.Control
                name="nome"
                id='nome'
                className={styles['input-control']}
                defaultValue={isLoading ? 'Carregando...' : userData?.name}
                required
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <FormLabel htmlFor='cpf'>CPF:</FormLabel>
              <CpfInput
                id="cpf"
                className={`form-control ${styles['input-control']}`}
                defaultValue={isLoading ? 'Carregando...' : userData?.cpf}
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-3">
        <Row>
          <Col md={6}>
            <Form.Group>
              <FormLabel htmlFor='email'>E-mail:</FormLabel>
              <Form.Control
                name="email"
                type='email'
                className={styles['input-control']}
                id='email'
                defaultValue={isLoading ? 'Carregando...' : userData?.email}
                disabled={isLoading}
                />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <FormLabel htmlFor='email-alternativo'>E-mail alternativo:</FormLabel>
              <Form.Control
                name="email-alternativo"
                id='email-alternativo'
                className={styles['input-control']}
                type="email"
                defaultValue={isLoading ? 'Carregando...' : userData?.email_alternativo}
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-3">
        <Row>
          <Col md={4}>
            <Form.Group>
              <FormLabel htmlFor='birthday'>Data de Nascimento:</FormLabel>
              <Form.Control
                name="birthday"
                id='birthday'
                className={styles['input-control']}
                type='date'
                defaultValue={isLoading ? '' : userData?.birthday}
                disabled={isLoading}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  const age = Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365));
                  setUserAge(age);

                }}
              />
            </Form.Group>
          </Col>
                    <Col md={4}>
            <Form.Group>
              <FormLabel htmlFor='civil_status'>Estado Civil:</FormLabel>
              <Select
                name="civil_status"
                id="civil_status"
                classNames={{
                  control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                  menu: () => styles['select-menu'],
                  option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                  container: () => styles['select-container'],
                }}
                options={civilStatus.map(s => ({
                  label: s.name,
                  value: s.code, // aqui é string, como "M" ou "F"
                }))}
                value={selectedCivilStatus ? { label: civilStatus.find(s => s.code === selectedCivilStatus)?.name || '', value: selectedCivilStatus } : null}
                onChange={(selected) => setSelectedCivilStatus(selected?.value ?? null)}
                isDisabled={isLoading}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <FormLabel htmlFor='sexo-biologico'>Sexo biológico:</FormLabel>
              <Select
                name="sexo-biologico"
                id="sexo-biologico"
                classNames={{
                  control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                  menu: () => styles['select-menu'],
                  option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                  container: () => styles['select-container'],
                }}
                options={sexos.map(s => ({
                  label: s.name,
                  value: s.code, // aqui é string, como "M" ou "F"
                }))}
                value={selectedSexo ? { label: sexos.find(s => s.code === selectedSexo)?.name || '', value: selectedSexo } : null}
                onChange={(selected) => setSelectedSexo(selected?.value ?? null)}
                isDisabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-3">
        <Row>
          <Col md>
            <Form.Group>
              <FormLabel id='personal-telefone'>Telefone:</FormLabel>
              <PhoneNumberInput
                id="personal-telefone"
                className={styles['input-control']}
                defaultValue={userData?.telefone ? `+${userData.telefone.ddi}${userData.telefone.numero}` : undefined}
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>

      <AddressForm
        id={"main"}
        defaultCep={userData?.address?.cep}
        defaultComplemento={userData?.address?.complemento}
        defaultNumero={userData?.address?.numero}
        disabled={isLoading}
      />

      <h4>Informações do Paciente</h4>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <FormLabel htmlFor='profissao' >Profissão:</FormLabel>
            <Form.Control
              name="profissao"
              className={styles['input-control']}
              id='profissao'
              defaultValue={isLoading ? 'Carregando...' : userData?.patient?.profissao}
              disabled={isLoading}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <FormLabel htmlFor='indicacao'>Indicação:</FormLabel>
            <Select
              name="indicacao"
              id="indicacao"
              classNames={{
                control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                menu: () => styles['select-menu'],
                option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                container: () => styles['select-container'],
              }}
              isSearchable={true}
              options={indication.map(i => ({ label: i.name, value: i.id }))}
              onChange={(values) => setSelectedIndicacao(values?.value ?? null)}
              value={selectedIndicacao ? { label: indication.find(i => i.id === selectedIndicacao)?.name || '', value: selectedIndicacao } : null}
              isDisabled={isLoading}
            />
          </Form.Group>
        </Col>
      </Row>
      {(userAge ? userAge < 18 : false) && (
        <Form.Group className="mb-3">
          <FormLabel htmlFor='responsavel'>Responsável Legal:</FormLabel>
          <Select
            name="responsavel"
            id="responsavel"
            classNames={{
              control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
              menu: () => styles['select-menu'],
              option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
              container: () => styles['select-container'],
            }}
            isSearchable={true}
            options={users.map(u => ({ label: u.name, value: u.id }))}
            onChange={(value) => setSelectedResponsavel(value?.value ?? null)}
            value={selectedResponsavel ? { label: users.find(u => u.id === selectedResponsavel)?.name || '', value: selectedResponsavel } : null}
            isDisabled={isLoading}
          />
        </Form.Group>
      )}
    </>
  );
};

export default ClinicPatientForm;
